import * as React from 'react';
import { styled } from '@visualfabriq/vf-ui-kit';

export type ListMoreProps = React.PropsWithChildren<{
  loading: boolean;
  onLoadMore: () => void;
  scrollOffset?: number;
  className?: string;
}>;

export function More(props: ListMoreProps) {
  const { loading, onLoadMore, scrollOffset = 200, className, children } = props;
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const lastCallAtRef = React.useRef(0);

  const maybeLoadMore = React.useCallback(() => {
    if (loading) return;

    const now = Date.now();
    if (now - lastCallAtRef.current < 250) return;

    const el = sentinelRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const distanceToViewportBottom = rect.top - window.innerHeight;
    if (distanceToViewportBottom <= scrollOffset) {
      lastCallAtRef.current = now;
      onLoadMore();
    }
  }, [loading, onLoadMore, scrollOffset]);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    // If IO is not supported, fall back to a scroll/resize listener.
    if (typeof IntersectionObserver === 'undefined') {
      const onEvent = () => maybeLoadMore();
      window.addEventListener('scroll', onEvent, { passive: true });
      window.addEventListener('resize', onEvent);
      maybeLoadMore();
      return () => {
        window.removeEventListener('scroll', onEvent);
        window.removeEventListener('resize', onEvent);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        maybeLoadMore();
      },
      {
        root: null,
        rootMargin: `0px 0px ${scrollOffset}px 0px`,
        threshold: 0,
      },
    );

    observer.observe(el);
    maybeLoadMore();

    return () => observer.disconnect();
  }, [maybeLoadMore, scrollOffset]);

  // When loading flips back to false while the sentinel is still visible,
  // IO may not fire again; do a direct check.
  React.useEffect(() => {
    if (!loading) maybeLoadMore();
  }, [loading, maybeLoadMore]);

  return (
    <MoreRoot className={className} aria-busy={loading}>
      {loading ? children : null}
      <MoreSentinel ref={sentinelRef} />
    </MoreRoot>
  );
}

const MoreRoot = styled('div', () => ({
  width: '100%',
}));

const MoreSentinel = styled('div', () => ({
  height: '1px',
  width: '100%',
}));
